import { COPY as SHARED_CONTACTS_COPY } from "@/constants/campaignContacts";
import type { AdminCampaignRow } from "@/interfaces/adminCampaigns";
import type { CsvColumn } from "@/lib/csv";
import { formatDate } from "@/utils/date-format";
import { formatRate } from "@/utils/number-format";

/** Placeholder for a null cell — open_rate, redemption_rate, expiry, account_lead. */
export const EMPTY_CELL = "—";

/** Chip field ids, matching the querystring keys `toListParams` maps them onto. */
export const FILTER_IDS = {
  organization: "organization_id",
  status: "status",
  campaignType: "campaign_type",
  sendMethod: "send_method",
  scheduled: "scheduled",
  expiry: "expiry",
} as const;

/** Rows per request — the API's MAX_PAGE_SIZE; anything larger is a 400. */
export const PAGE_SIZE = 100;

/** Every configurable column id, in the ticket's display order. */
export type AdminCampaignColumnId =
  | "account_lead"
  | "collection_value"
  | "status"
  | "recipients"
  | "scheduled_at"
  | "expiry"
  | "campaign_type"
  | "send_method"
  | "open_rate"
  | "redemption_rate"
  | "bounces"
  | "scheduled_reminders"
  | "thank_you_messages";

export const ADMIN_CAMPAIGN_COLUMN_IDS: readonly AdminCampaignColumnId[] = [
  "account_lead",
  "collection_value",
  "status",
  "recipients",
  "scheduled_at",
  "expiry",
  "campaign_type",
  "send_method",
  "open_rate",
  "redemption_rate",
  "bounces",
  "scheduled_reminders",
  "thank_you_messages",
];

/** Column id -> header label; every one of the 13 defaults to visible. */
export const COLUMN_LABELS: Record<AdminCampaignColumnId, string> = {
  account_lead: "Account Lead",
  collection_value: "Collection Value",
  status: "Status",
  recipients: "Recipients",
  scheduled_at: "Scheduled Date",
  expiry: "Expiration Date",
  campaign_type: "Campaign Type",
  send_method: "Send Method",
  open_rate: "Open Rate",
  redemption_rate: "Redemption Rate",
  bounces: "Bounces",
  scheduled_reminders: "Scheduled Reminders",
  thank_you_messages: "Thank You Messages",
};

/** The three fixed columns -- always visible, outside the column configurator. */
export const FIXED_COLUMN_LABELS = {
  action: "Action",
  campaignName: "Campaign Name",
  client: "Client",
} as const;

/**
 * All user-facing copy for the tab, its row menu and its three modals -- components import from
 * here rather than hardcoding a string, mirroring constants/sendQueueCampaigns.ts's COPY object.
 */
export const COPY = {
  emptyState: "No campaigns match these filters.",
  searchPlaceholder: "Search and filter campaigns",
  /** BlotterFilterBar field labels, in the ticket's display order. */
  filterLabels: {
    organization: "Client/Org",
    status: "Status",
    campaignType: "Campaign Type",
    sendMethod: "Send Method",
    scheduledDate: "Scheduled Date",
    expirationDate: "Expiration Date",
  },
  /** cmpRowMenu item labels. */
  rowMenu: {
    impersonate: "Impersonate",
    manageContacts: "Manage Contacts",
    manageGiftLinks: "Manage Gift Links",
    pause: "Pause",
    resume: "Resume",
    cancel: "Cancel Campaign",
    disable: "Disable",
    enable: "Enable",
    archive: "Archive",
    restore: "Restore",
  },
  /**
   * cmpContactsOverlay -- the Manage Contacts modal. Both tabs open the same dialog over the same
   * rows, so every string here is the shared one from constants/campaignContacts.ts, never a copy.
   */
  contactsModal: {
    title: SHARED_CONTACTS_COPY.title,
    searchPlaceholder: SHARED_CONTACTS_COPY.searchPlaceholder,
    allStatuses: SHARED_CONTACTS_COPY.allStatuses,
    columns: {
      name: SHARED_CONTACTS_COPY.columns.name,
      email: SHARED_CONTACTS_COPY.columns.email,
      status: SHARED_CONTACTS_COPY.columns.status,
      delivered: SHARED_CONTACTS_COPY.columns.delivered,
      campaignUrl: SHARED_CONTACTS_COPY.columns.url,
    },
    /** Above the table; either banner disables every row-action icon. */
    lockBanner: {
      paused: SHARED_CONTACTS_COPY.lock.paused,
      ended: SHARED_CONTACTS_COPY.lock.closed,
    },
    close: SHARED_CONTACTS_COPY.close,
    exportCsv: SHARED_CONTACTS_COPY.exportCsv,
  },
  /**
   * cmpRemoveOverlay -- the Remove Contact sub-modal. The ticket describes the warning
   * without quoting it ("mentions explicitly when the gift was already delivered"); this wording
   * is lifted verbatim from the prototype (reference/prototype-campaigns-extract.md), the
   * behavior source for anything the ticket leaves unquoted.
   */
  removeContactModal: {
    title: "Remove contact",
    warningDelivered:
      "If you remove this contact from the campaign, their redemption link will no longer be " +
      "valid and they will not be able to claim their gift, even though they may have received " +
      "an email from you telling them they have a gift. Are you sure you want to remove this " +
      "contact?",
    cancel: "Cancel",
    confirm: "Remove contact",
  },
  /** cmpEmailOverlay -- the personalized email preview sub-modal. */
  emailPreview: {
    to: (name: string, email: string) => `To: ${name} <${email}>`,
    sender: (orgName: string) => `${orgName} via Gifts for Good · giftforward@giftsforgood.com`,
  },
  /** Status-action toasts. */
  toast: {
    impersonating: (campaignName: string) => `Opening: ${campaignName}`,
    paused: (campaignName: string) => `Paused: ${campaignName}`,
    resumed: (campaignName: string) => `Resumed: ${campaignName}`,
    cancelled: (campaignName: string) => `Cancelled: ${campaignName}`,
    disabled: (campaignName: string) => `Disabled: ${campaignName}`,
    enabled: (campaignName: string) => `Enabled: ${campaignName}`,
    archived: (campaignName: string) => `Archived: ${campaignName}`,
    restored: (campaignName: string) => `Restored: ${campaignName}`,
  },
  /** Confirm dialog before pausing an Active campaign -- the ticket's wording. */
  pauseLiveConfirm:
    "Pausing a live campaign stops any more emails going out, including reminder emails. If " +
    "recipients have already received their gift emails, pausing a campaign will not prevent " +
    "them from viewing the gift collection or redeeming a gift.",
  /** Confirm dialog before archiving -- the ticket's exact wording. */
  archiveConfirm: (campaignName: string) =>
    `Archive "${campaignName}"? It will be marked archived and hidden from clients.`,
  confirmModal: {
    pauseTitle: "Pause this campaign?",
    pauseAction: "Pause",
    archiveTitle: "Archive this campaign?",
    archiveAction: "Archive",
    dismiss: "Cancel",
  },
} as const;

/**
 * Campaigns CSV export headers/values, one entry per possible column. The helper that
 * assembles the exported file picks entries here in the caller's visible-column order; Action has
 * no export column, since it holds a menu button and no data.
 */
export const CAMPAIGNS_CSV_COLUMNS: Record<
  "campaignName" | "client" | AdminCampaignColumnId,
  CsvColumn<AdminCampaignRow>
> = {
  campaignName: { label: FIXED_COLUMN_LABELS.campaignName, value: (row) => row.name },
  client: { label: FIXED_COLUMN_LABELS.client, value: (row) => row.organization },
  account_lead: { label: COLUMN_LABELS.account_lead, value: (row) => row.accountLead ?? "" },
  collection_value: {
    label: COLUMN_LABELS.collection_value,
    value: (row) => String(row.collectionValue),
  },
  status: { label: COLUMN_LABELS.status, value: (row) => row.status },
  recipients: { label: COLUMN_LABELS.recipients, value: (row) => String(row.recipients) },
  // Both dates and both rates export exactly what the cell shows, never the raw wire value.
  // Empty, not the table's em dash: a spreadsheet cell with no date should be blank so the
  // column still sorts and filters as dates.
  scheduled_at: { label: COLUMN_LABELS.scheduled_at, value: (row) => formatDate(row.scheduledAt) },
  expiry: {
    label: COLUMN_LABELS.expiry,
    value: (row) => (row.expiry ? formatDate(row.expiry) : ""),
  },
  campaign_type: { label: COLUMN_LABELS.campaign_type, value: (row) => row.campaignType },
  send_method: { label: COLUMN_LABELS.send_method, value: (row) => row.sendMethod },
  open_rate: { label: COLUMN_LABELS.open_rate, value: (row) => formatRate(row.openRate) },
  redemption_rate: {
    label: COLUMN_LABELS.redemption_rate,
    value: (row) => formatRate(row.redemptionRate),
  },
  bounces: { label: COLUMN_LABELS.bounces, value: (row) => String(row.bounces) },
  scheduled_reminders: {
    label: COLUMN_LABELS.scheduled_reminders,
    value: (row) => String(row.scheduledReminders),
  },
  thank_you_messages: {
    label: COLUMN_LABELS.thank_you_messages,
    value: (row) => String(row.thankYouMessages),
  },
};
